import { useList, useDelete, useUpdate, useInvalidate } from "@refinedev/core";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  MessageCircle,
  Users,
  Plus,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  Megaphone,
  MessagesSquare,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { FormHeader } from "@/components/forms";
import { type TelegramChannel, type TelegramChatType, CHAT_TYPE_LABELS } from "./types";
import { DataTable, TableToolbar, type Column, type SortState, type BulkAction } from "@/components/tables";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { usePersistedState } from "@/hooks/use-persisted-state";
import { useScrollRestoration } from "@/hooks/use-scroll-restoration";
import { notify } from "@/lib/notify";

const dateFormatter = new Intl.DateTimeFormat("ru-RU", {
  dateStyle: "medium",
  timeStyle: "short",
});

const formatDateTime = (value: string | null | undefined) => {
  if (!value) {return "—";}
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : dateFormatter.format(date);
};

/**
 * Telegram channels list page with DataTable
 */
/**
 * Иконка для типа чата
 */
function ChatTypeIcon({ type }: { type: TelegramChatType }) {
  switch (type) {
    case 'channel':
      return <Megaphone className="h-3.5 w-3.5" />;
    case 'group':
    case 'supergroup':
      return <MessagesSquare className="h-3.5 w-3.5" />;
    default:
      return <MessageCircle className="h-3.5 w-3.5" />;
  }
}

/**
 * Бейдж для типа чата
 */
function ChatTypeBadge({ type }: { type: TelegramChatType }) {
  const variant = type === 'channel' ? 'default' : 'secondary';
  return (
    <Badge variant={variant} className="gap-1 font-normal">
      <ChatTypeIcon type={type} />
      {CHAT_TYPE_LABELS[type] ?? type}
    </Badge>
  );
}

export function TelegramChannelsList() {
  useScrollRestoration();
  const navigate = useNavigate();
  const [page, setPage] = usePersistedState("telegram-channels:page", 1);
  const [sort, setSort] = usePersistedState<SortState | undefined>("telegram-channels:sort", { field: "title", order: "asc" });
  const [deleteItems, setDeleteItems] = useState<TelegramChannel[]>([]);
  const [search, setSearch] = usePersistedState("telegram-channels:search", "");
  const [typeFilter, setTypeFilter] = usePersistedState<TelegramChatType | "all">("telegram-channels:typeFilter", "all");
  const pageSize = 50;

  // Собираем фильтры
  const filters: Array<{ field: string; operator: "contains" | "eq"; value: string }> = [];
  if (search) {
    filters.push({ field: "search", operator: "contains", value: search });
  }
  if (typeFilter !== "all") {
    filters.push({ field: "type", operator: "eq", value: typeFilter });
  }

  const { result, query } = useList<TelegramChannel>({
    resource: "telegram-channels",
    pagination: { currentPage: page, pageSize, mode: "server" },
    sorters: sort ? [{ field: sort.field, order: sort.order }] : [],
    filters,
  });

  const channels = result?.data ?? [];
  const total = result?.total ?? 0;
  const isLoading = query?.isLoading ?? false;

  const { mutate: deleteChannel, mutation: deleteMutation } = useDelete();
  const isDeleting = deleteMutation.isPending;
  const { mutate: updateChannel } = useUpdate();
  const invalidate = useInvalidate();

  const handleToggleActive = (items: TelegramChannel[]) => {
    if (items.length === 0) {return;}
    const activating = items.length > 0 && !items[0].isActive;
    items.forEach(item => {
      updateChannel(
        {
          resource: "telegram-channels",
          id: item.id,
          values: { isActive: !item.isActive },
          successNotification: false,
        },
        {
          onSuccess: () => {
            invalidate({
              resource: "telegram-channels",
              invalidates: ["list"],
            });
          },
        },
      );
    });
    notify.success(`${activating ? "Активировано" : "Деактивировано"}: ${items.length}`);
  };

  const handleDelete = () => {
    if (deleteItems.length === 0) {return;}

    const count = deleteItems.length;
    for (const item of deleteItems) {
      deleteChannel({ resource: "telegram-channels", id: item.id, successNotification: false });
    }
    notify.success(`Удалено: ${count}`);
    setDeleteItems([]);
  };

  const columns: Column<TelegramChannel>[] = [
    {
      key: "title",
      header: "Название",
      sortable: true,
      render: (_, row) => (
        <div className="flex items-center gap-3">
          {row.photoUrl ? (
            <img
              src={row.photoUrl}
              className="h-10 w-10 rounded-full object-cover border"
              alt={row.title}
            />
          ) : (
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
              <ChatTypeIcon type={row.type} />
            </div>
          )}
          <div className="flex flex-col">
            <span className="font-medium text-sm">{row.title}</span>
            <span className="text-xs text-muted-foreground">
              {row.telegramId}
            </span>
          </div>
        </div>
      ),
    },
    {
      key: "type",
      header: "Тип",
      sortable: true,
      render: (_, row) => <ChatTypeBadge type={row.type} />,
    },
    {
      key: "username",
      header: "Ссылка",
      sortable: true,
      render: (_, row) => (
        <div className="flex flex-col gap-0.5">
          {row.username ? (
            <a
              href={`https://t.me/${row.username}`}
              target="_blank"
              className="text-sm text-blue-500 hover:underline"
            >
              @{row.username}
            </a>
          ) : (row.inviteLink ? (
            <a
              href={row.inviteLink}
              target="_blank"
              className="text-xs text-muted-foreground hover:underline truncate max-w-[150px]"
            >
              Ссылка приглашения
            </a>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ))}
        </div>
      ),
    },
    {
      key: "activeMemberCount",
      header: "Подписчики",
      sortable: true,
      render: (_, row) => (
        <HoverCard>
          <HoverCardTrigger asChild>
            <div className="flex items-center gap-1.5 cursor-pointer hover:text-primary transition-colors w-fit">
              <Users className="h-4 w-4 text-muted-foreground/70" />
              <span className="font-medium">{row.activeMemberCount ?? 0}</span>
            </div>
          </HoverCardTrigger>
          <HoverCardContent className="w-64 p-3">
            <div className="space-y-2">
              <h5 className="text-xs font-semibold text-muted-foreground border-b pb-1">
                Статистика канала
              </h5>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Наши подписчики:</span>
                  <span className="font-medium">{row.activeMemberCount ?? 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Всего в Telegram:</span>
                  <span className="text-muted-foreground">{row.memberCount ?? "—"}</span>
                </div>
              </div>
            </div>
          </HoverCardContent>
        </HoverCard>
      ),
    },
    {
      key: "createdAt",
      header: "Добавлен",
      sortable: true,
      render: (val) => formatDateTime(typeof val === "string" ? val : null),
    },
    {
      key: "updatedAt",
      header: "Обновлён",
      sortable: true,
      render: (val) => formatDateTime(typeof val === "string" ? val : null),
    },
    {
      key: "botName",
      header: "Бот",
      sortable: true,
      render: (val) => (
        <span className="text-sm">
          {val ? (val as string) : "—"}
        </span>
      ),
    },
  ];

  const bulkActions: BulkAction<TelegramChannel>[] = [
    {
      label: "Редактировать",
      icon: <Pencil className="h-4 w-4" />,
      onClick: (items) => {
        if (items.length === 1) {
          navigate(`/telegram-channels/edit/${items[0].id}`);
        }
      },
      showOnlyWhenSelected: true,
    },
    {
      label: "Активировать",
      icon: <Eye className="h-4 w-4" />,
      onClick: (items) => handleToggleActive(items.filter(i => !i.isActive)),
      showOnlyWhenSelected: true,
    },
    {
      label: "Деактивировать",
      icon: <EyeOff className="h-4 w-4" />,
      onClick: (items) => handleToggleActive(items.filter(i => i.isActive)),
      variant: "outline",
      showOnlyWhenSelected: true,
    },
    {
      label: "Удалить",
      icon: <Trash2 className="h-4 w-4" />,
      onClick: setDeleteItems,
      variant: "destructive",
      showOnlyWhenSelected: true,
    },
  ];

  return (
    <div className="space-y-6">
      <FormHeader
        title="Telegram каналы и чаты"
        actions={
          <Button onClick={() => navigate("/telegram-channels/create")}>
            <Plus className="h-4 w-4 mr-2" />
            Добавить
          </Button>
        }
        sticky
      />

      <div className="flex flex-wrap items-center gap-4">
        <TableToolbar
          searchValue={search}
          onSearchChange={(v) => { setSearch(v); setPage(1); }}
          searchPlaceholder="Поиск по названию..."
        />
        <Select
          value={typeFilter}
          onValueChange={(v) => { setTypeFilter(v as TelegramChatType | "all"); setPage(1); }}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Все типы" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Все типы</SelectItem>
            <SelectItem value="channel">
              <span className="flex items-center gap-2">
                <Megaphone className="h-4 w-4" />
                Каналы
              </span>
            </SelectItem>
            <SelectItem value="group">
              <span className="flex items-center gap-2">
                <MessagesSquare className="h-4 w-4" />
                Группы
              </span>
            </SelectItem>
            <SelectItem value="supergroup">
              <span className="flex items-center gap-2">
                <MessagesSquare className="h-4 w-4" />
                Супергруппы
              </span>
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <DataTable
        columns={columns}
        data={channels}
        total={total}
        page={page}
        pageSize={pageSize}
        isLoading={isLoading}
        onPageChange={setPage}
        onRowClick={(row) => navigate(`/telegram-channels/edit/${row.id}`)}
        emptyMessage="Нет каналов"
        selectable
        rowKey="id"
        sort={sort}
        onSortChange={setSort}
        bulkActions={bulkActions}
      />

      <AlertDialog open={deleteItems.length > 0} onOpenChange={() => setDeleteItems([])}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteItems.length === 1
                ? "Удалить канал?"
                : `Удалить ${deleteItems.length} каналов?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteItems.length === 1 ? (
                <>
                  Вы уверены, что хотите удалить канал «{deleteItems[0].title || deleteItems[0].username || deleteItems[0].telegramId}»?
                  Это действие нельзя отменить.
                </>
              ) : (
                <>
                  Вы уверены, что хотите удалить {deleteItems.length} каналов?
                  Это действие нельзя отменить.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
